import { FormSelect } from '@systemeio/ui'
import {
  BlogEntityEnum,
  BlogEntityTitleEnum,
  BlogPageEnum,
} from 'modules/automation-rules/enums/automation-rule-enum'
import { defaultTriggerFilters } from 'modules/automation-rules/triggers/constants/default-trigger-filters'
import { triggerActionLocoKeys } from 'modules/automation-rules/triggers/constants/trigger-loco-keys'
import {
  AutomationRuleTriggersDataEnum,
  AutomationRuleTriggersEnum,
} from 'modules/automation-rules/triggers/enums/trigger-enum'
import { useTriggersData } from 'modules/automation-rules/triggers/hooks/use-triggers-data'
import {
  AutomationRuleFilterInterface,
  RuleBlogEntityFilterInterface,
  RuleTriggerFilterInterface,
} from 'modules/automation-rules/types/automation-rule-filter-interface'
import React, { useEffect } from 'react'
import { FilterableItemType } from 'shared/components/listing-layout'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useAllowedTriggers } from './use-allowed-triggers'

export const useFilterableTriggerData = (responseFilter: AutomationRuleFilterInterface) => {
  const { t } = useLocoTranslation()

  const blogEntityData: RuleBlogEntityFilterInterface[] = Object.values(BlogEntityEnum).map(
    key => ({
      caption: t(BlogEntityTitleEnum[key]),
      id: BlogEntityEnum[key],
    }),
  )

  const allowedTriggers = useAllowedTriggers()

  const triggerData: RuleTriggerFilterInterface[] = allowedTriggers.map(slug => ({
    id: slug,
    caption: t(triggerActionLocoKeys[slug]),
  }))

  const { triggersData, setShouldFetch } = useTriggersData(responseFilter)

  const {
    tagData,
    campaignData,
    blogData,
    blogPagesData,
    blogPostsData,
    communityData,
    emailData,
    emailLinksData,
    courseData,
    moduleData,
    lectureData,
    courseBundleData,
    webinarData,
    funnelsPageVisitedData,
    funnelStepsPageVisitedData,
    funnelsSaleCanceledData,
    funnelStepsSaleCanceledData,
    funnelsWebinarData,
    funnelsNewSaleData,
    funnelStepsNewSaleData,
    funnelStepsFormSubscribeData,
    funnelsFormSubscribeData,
    funnelsSubscriptionPaymentFailedData,
    funnelStepsSubscriptionPaymentFailedData,
    digitalStoresData,
    digitalStoreOptInsData,
    eventsData,
    websiteData,
    websitePageData,
  } = triggersData

  useEffect(() => {
    setShouldFetch(prev => ({
      ...prev,
      [AutomationRuleTriggersDataEnum.blog_page_form_subscribed]: !!responseFilter.blogId,

      [AutomationRuleTriggersDataEnum.blog_page_form_subscribed_page]:
        !!responseFilter.blogPageEntityId,

      [AutomationRuleTriggersDataEnum.blog_page_form_subscribed_post]:
        !!responseFilter.blogPostEntityId,

      [AutomationRuleTriggersDataEnum.form_subscribed]: !!responseFilter.formSubscribeFunnelId,

      [AutomationRuleTriggersDataEnum.form_subscribed_funnel_step]:
        !!responseFilter.formSubscribeFunnelStepId,

      [AutomationRuleTriggersDataEnum.new_sale]: !!responseFilter.newSaleFunnelId,

      [AutomationRuleTriggersDataEnum.new_sale_funnel_step]: !!responseFilter.newSaleFunnelStepId,

      [AutomationRuleTriggersDataEnum.sale_canceled]: !!responseFilter.cancelSaleFunnelId,

      [AutomationRuleTriggersDataEnum.sale_canceled_funnel_step]:
        !!responseFilter.cancelSaleFunnelStepId,

      [AutomationRuleTriggersDataEnum.page_visited]: !!responseFilter.pageVisitedFunnelId,

      [AutomationRuleTriggersDataEnum.page_visited_funnel_step]:
        !!responseFilter.pageVisitedFunnelStepId,

      [AutomationRuleTriggersDataEnum.registered_to_webinar]: !!responseFilter.webinarFunnelId,

      [AutomationRuleTriggersDataEnum.registered_to_webinar_funnel_step]:
        !!responseFilter.webinarId,

      [AutomationRuleTriggersDataEnum.course]: !!responseFilter.triggerCourseId,

      [AutomationRuleTriggersDataEnum.module]: !!responseFilter.triggerModuleId,

      [AutomationRuleTriggersDataEnum.lecture]: !!responseFilter.triggerLectureId,

      [AutomationRuleTriggersDataEnum.enrolled_in_course_bundle]:
        !!responseFilter.triggerCourseBundleId,

      [AutomationRuleTriggersDataEnum.enrolled_in_community]: !!responseFilter.triggerCommunityId,

      [AutomationRuleTriggersDataEnum.email]: !!responseFilter.triggerMailingId,

      [AutomationRuleTriggersDataEnum.email_link]: !!responseFilter.triggerLinkClickedId,

      [AutomationRuleTriggersDataEnum.tag]: !!responseFilter.triggerTagId,

      [AutomationRuleTriggersDataEnum.campaign_completed]: !!responseFilter.triggerCampaignId,

      [AutomationRuleTriggersDataEnum.subscription_payment_failed]:
        !!responseFilter.subscriptionFailedFunnelId,

      [AutomationRuleTriggersDataEnum.subscription_payment_failed_funnel_step]:
        !!responseFilter.subscriptionFailedFunnelStepId,

      [AutomationRuleTriggersDataEnum.digital_store_form_subscribed]:
        !!responseFilter.triggerDigitalStoreId,

      [AutomationRuleTriggersDataEnum.booking_created]: !!responseFilter.triggerBookingEventId,
    }))
  }, [responseFilter, setShouldFetch])

  const filterableTrigger: FilterableItemType<AutomationRuleFilterInterface>[] = [
    {
      field: 'trigger',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          data={triggerData}
          key={'trigger-rule-trigger-filter'}
          placeholder={t('dashboard.automation_rule.choose_trigger_type')}
          value={value}
          onChange={data => onChangeState({ ...state, trigger: data, ...defaultTriggerFilters })}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.tag_added ||
        responseFilter.trigger === AutomationRuleTriggersEnum.tag_removed,
      field: 'triggerTagId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({ ...prev, [AutomationRuleTriggersDataEnum.tag]: true }))
          }
          data={tagData}
          key={'trigger-rule-tag-filter'}
          placeholder={t('dashboard.automation_rule.choose_tag')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.campaign_completed,
      field: 'triggerCampaignId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.campaign_completed]: true,
            }))
          }
          data={campaignData}
          key={'trigger-rule-campaign-filter'}
          placeholder={t('dashboard.automation_rule.choose_campaign')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.blog_page_form_subscribed,
      field: 'blogId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.blog_page_form_subscribed]: true,
            }))
          }
          data={blogData}
          key={'trigger-rule-blog-filter'}
          placeholder={t('dashboard.automation_rule.choose_blog')}
          value={value}
          onChange={data =>
            onChangeState({
              ...state,
              blogId: data,
              blogPageEntityId: undefined,
              blogPostEntityId: undefined,
              blogEntity: undefined,
            })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.blog_page_form_subscribed &&
        responseFilter.blogId !== undefined,
      field: 'blogEntity',
      onRender: (value, onChange) => (
        <FormSelect
          data={blogEntityData}
          key={'trigger-rule-blog-entity-filter'}
          placeholder={t('dashboard.automation_rule.choose_blog_entity')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.blog_page_form_subscribed &&
        responseFilter.blogId !== undefined &&
        (responseFilter.blogEntity === BlogEntityEnum.blog_page ||
          responseFilter.blogEntity === BlogPageEnum.blog_home ||
          responseFilter.blogEntity === BlogPageEnum.blog_static ||
          responseFilter.blogEntity === BlogPageEnum.blog_post_listing ||
          responseFilter.blogEntity === BlogPageEnum.blog_search),
      field: 'blogPageEntityId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.blog_page_form_subscribed_page]: true,
            }))
          }
          data={blogPagesData}
          key={'trigger-rule-blog-entity-pages-filter'}
          placeholder={t('dashboard.automation_rule.choose_blog_page')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.blog_page_form_subscribed &&
        responseFilter.blogId !== undefined &&
        responseFilter.blogEntity === BlogEntityEnum.blog_post_body,
      field: 'blogPostEntityId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.blog_page_form_subscribed_post]: true,
            }))
          }
          data={blogPostsData}
          key={'trigger-rule-blog-entity-posts-filter'}
          placeholder={t('dashboard.automation_rule.choose_blog_post')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.form_subscribed,
      field: 'formSubscribeFunnelId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.form_subscribed]: true,
            }))
          }
          data={funnelsFormSubscribeData}
          key={'trigger-rule-funnel-form-subscribed-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel')}
          value={value}
          onChange={data =>
            onChangeState({
              ...state,
              formSubscribeFunnelId: data,
              formSubscribeFunnelStepId: undefined,
            })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.form_subscribed &&
        responseFilter.formSubscribeFunnelId !== undefined,
      field: 'formSubscribeFunnelStepId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.form_subscribed_funnel_step]: true,
            }))
          }
          data={funnelStepsFormSubscribeData}
          key={'trigger-rule-funnel-step-form-subscribed-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel_step')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.website_form_subscribed,
      field: 'triggerWebsiteId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.website_form_subscribed]: true,
            }))
          }
          data={websiteData}
          key={'trigger-rule-website-form-subscribed-filter'}
          placeholder={t('dashboard.automation_rule.choose_website')}
          value={value}
          onChange={data =>
            onChangeState({
              ...state,
              triggerWebsiteId: data,
              triggerWebsitePageId: undefined,
            })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.website_form_subscribed &&
        responseFilter.triggerWebsiteId !== undefined,
      field: 'triggerWebsitePageId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.website_form_subscribed]: true,
            }))
          }
          data={websitePageData}
          key={'trigger-rule-website-page-form-subscribed-filter'}
          placeholder={t('dashboard.automation_rule.choose_website_page')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.subscription_payment_failed,
      field: 'subscriptionFailedFunnelId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.subscription_payment_failed]: true,
            }))
          }
          data={funnelsSubscriptionPaymentFailedData}
          key={'trigger-rule-subscription-payment-failed-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel')}
          value={value}
          onChange={data =>
            onChangeState({
              ...state,
              subscriptionFailedFunnelId: data,
              subscriptionFailedFunnelStepId: undefined,
            })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.subscription_payment_failed &&
        responseFilter.subscriptionFailedFunnelId !== undefined,
      field: 'subscriptionFailedFunnelStepId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.subscription_payment_failed_funnel_step]: true,
            }))
          }
          data={funnelStepsSubscriptionPaymentFailedData}
          key={'trigger-rule-funnel-step-subscription-payment-failed-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel_step')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.new_sale,
      field: 'newSaleFunnelId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({ ...prev, [AutomationRuleTriggersDataEnum.new_sale]: true }))
          }
          data={funnelsNewSaleData}
          key={'trigger-rule-funnel-sale-new-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel')}
          value={value}
          onChange={data =>
            onChangeState({
              ...state,
              newSaleFunnelId: data,
              newSaleFunnelStepId: undefined,
            })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.new_sale &&
        responseFilter.newSaleFunnelId !== undefined,
      field: 'newSaleFunnelStepId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.new_sale_funnel_step]: true,
            }))
          }
          data={funnelStepsNewSaleData}
          key={'trigger-rule-funnel-step-sale-new-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel_step')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.sale_canceled,
      field: 'cancelSaleFunnelId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.sale_canceled]: true,
            }))
          }
          data={funnelsSaleCanceledData}
          key={'trigger-rule-funnel-sale-canceled-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel')}
          value={value}
          onChange={data =>
            onChangeState({
              ...state,
              cancelSaleFunnelId: data,
              cancelSaleFunnelStepId: undefined,
            })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.sale_canceled &&
        responseFilter.cancelSaleFunnelId !== undefined,
      field: 'cancelSaleFunnelStepId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.sale_canceled_funnel_step]: true,
            }))
          }
          data={funnelStepsSaleCanceledData}
          key={'trigger-rule-funnel-step-sale-canceled-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel_step')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.page_visited,
      field: 'pageVisitedFunnelId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.page_visited]: true,
            }))
          }
          data={funnelsPageVisitedData}
          key={'trigger-rule-funnel-page-visited-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel')}
          value={value}
          onChange={data =>
            onChangeState({
              ...state,
              pageVisitedFunnelId: data,
              pageVisitedFunnelStepId: undefined,
            })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.page_visited &&
        responseFilter.pageVisitedFunnelId !== undefined,
      field: 'pageVisitedFunnelStepId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.page_visited_funnel_step]: true,
            }))
          }
          data={funnelStepsPageVisitedData}
          key={'trigger-rule-funnel-step-page-visited-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel_step')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.registered_to_webinar,
      field: 'webinarFunnelId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.registered_to_webinar]: true,
            }))
          }
          data={funnelsWebinarData}
          key={'trigger-rule-funnel-webinar-filter'}
          placeholder={t('dashboard.automation_rule.choose_funnel')}
          value={value}
          onChange={data =>
            onChangeState({ ...state, webinarFunnelId: data, webinarId: undefined })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.registered_to_webinar &&
        responseFilter.webinarFunnelId !== undefined,
      field: 'webinarId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.registered_to_webinar_funnel_step]: true,
            }))
          }
          data={webinarData}
          key={'trigger-rule-webinar-filter'}
          placeholder={t('dashboard.automation_rule.choose_webinar')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.enrolled_in_course ||
        responseFilter.trigger === AutomationRuleTriggersEnum.course_completed ||
        responseFilter.trigger === AutomationRuleTriggersEnum.module_completed ||
        responseFilter.trigger === AutomationRuleTriggersEnum.lecture_completed,
      field: 'triggerCourseId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.course]: true,
            }))
          }
          data={courseData}
          key={'trigger-rule-course-filter'}
          placeholder={t('dashboard.automation_rule.choose_course')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        (responseFilter.trigger === AutomationRuleTriggersEnum.module_completed ||
          responseFilter.trigger === AutomationRuleTriggersEnum.lecture_completed) &&
        responseFilter.triggerCourseId !== undefined,
      field: 'triggerModuleId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.module]: true,
            }))
          }
          data={moduleData}
          key={'trigger-rule-module-filter'}
          placeholder={t('dashboard.automation_rule.trigger.choose_module')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.lecture_completed &&
        responseFilter.triggerModuleId !== undefined,
      field: 'triggerLectureId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.lecture]: true,
            }))
          }
          data={lectureData}
          key={'trigger-rule-lecture-filter'}
          placeholder={t('dashboard.automation_rule.trigger.choose_lecture')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.enrolled_in_course_bundle,
      field: 'triggerCourseBundleId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.enrolled_in_course_bundle]: true,
            }))
          }
          data={courseBundleData}
          key={'trigger-rule-course-bundle-filter'}
          placeholder={t('dashboard.automation_rule.choose_course_bundle')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.enrolled_in_community,
      field: 'triggerCommunityId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.enrolled_in_community]: true,
            }))
          }
          data={communityData}
          key={'trigger-rule-community-filter'}
          placeholder={t('dashboard.automation_rule.choose_community')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.email_link_clicked ||
        responseFilter.trigger === AutomationRuleTriggersEnum.email_opened,
      field: 'triggerMailingId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.email]: true,
            }))
          }
          data={emailData}
          key={'trigger-rule-email-filter'}
          placeholder={t('dashboard.automation_rule.choose_email')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.email_link_clicked &&
        responseFilter.triggerMailingId !== undefined,
      field: 'triggerLinkClickedId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.email_link]: true,
            }))
          }
          data={emailLinksData}
          key={'trigger-rule-email-link-filter'}
          placeholder={t('dashboard.automation_rule.choose_email_link')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.digital_store_form_subscribed,
      field: 'triggerDigitalStoreId',
      onRender: (value, _, state, onChangeState) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.digital_store_form_subscribed]: true,
            }))
          }
          data={digitalStoresData}
          key={'trigger-rule-digital-store-filter'}
          placeholder={t('dashboard.automation_rule.choose_store')}
          value={value}
          onChange={data =>
            onChangeState({
              ...state,
              triggerDigitalStoreId: data,
            })
          }
        />
      ),
    },
    {
      shouldRender:
        responseFilter.trigger === AutomationRuleTriggersEnum.digital_store_form_subscribed &&
        responseFilter.triggerDigitalStoreId !== undefined,
      field: 'triggerDigitalStoreOptInId',
      onRender: (value, onChange) => (
        <FormSelect
          data={digitalStoreOptInsData}
          key={'trigger-rule-digital-store-optin-filter'}
          placeholder={t('dashboard.automation_rule.choose_store_optin')}
          value={value}
          onChange={onChange}
        />
      ),
    },
    {
      shouldRender: responseFilter.trigger === AutomationRuleTriggersEnum.booking_created,
      field: 'triggerBookingEventId',
      onRender: (value, onChange) => (
        <FormSelect
          onOpen={() =>
            setShouldFetch(prev => ({
              ...prev,
              [AutomationRuleTriggersDataEnum.booking_created]: true,
            }))
          }
          data={eventsData}
          key={'trigger-rule-event-filter'}
          placeholder={t('dashboard.automation_rule.choose_event')}
          value={value}
          onChange={onChange}
        />
      ),
    },
  ]
  return filterableTrigger
}
